import { sanityOriginClient } from './client';
import type { SanityContentPage } from './types';

/**
 * Generic, non-editorial pages live under their own prefix (FFH-2713), like
 * articles under /articles: no collisions with the WordPress root catch-all,
 * and one prefix per content type so the mobile app can route universal
 * links by path alone. Legal pages keep their fixed root routes.
 */
export const CONTENT_PAGES_PATH_PREFIX = '/pages';

export const buildContentPagePath = (slug: string): string =>
  `${CONTENT_PAGES_PATH_PREFIX}/${slug}`;

/**
 * Old WordPress /pages/* URLs that next.config.js permanently redirects to
 * root routes. A config redirect runs before page routing, so a document with
 * one of these slugs could never render; the route refuses them and the
 * Studio warns (mirrored in sanity/schemaTypes/contentPage.ts).
 */
export const RESERVED_CONTENT_PAGE_SLUGS = [
  'become-a-contributor',
  'become-an-affiliate',
  'contributor'
] as const;

export const isReservedContentPageSlug = (slug: string): boolean =>
  (RESERVED_CONTENT_PAGE_SLUGS as readonly string[]).includes(slug);

export const getAllContentPageSlugsQuery = `
  *[_type == "contentPage" && defined(slug.current)] { "slug": slug.current }
`;

export const contentPageBySlugQuery = `
  *[_type == "contentPage" && slug.current == $slug][0] {
    _type,
    title,
    slug,
    _updatedAt,
    heroImage {
      ...,
      asset->{
        _id,
        url,
        metadata { dimensions }
      }
    },
    body[]{
      ...,
      _type == "image" => {
        ...,
        asset->{
          _id,
          url,
          metadata { dimensions }
        }
      }
    },
    seo {
      metaTitle,
      metaDescription,
      ogImage { ..., asset->{ _id, url, metadata { dimensions } } },
      canonicalUrl,
      isHiddenFromSearch
    }
  }
`;

/**
 * Errors are left to propagate: a failed regeneration keeps the last good
 * page live, where swallowing the error would replace it with a 404.
 */
export const getContentPage = async (
  slug: string
): Promise<SanityContentPage | null> => {
  const page = await sanityOriginClient.fetch<SanityContentPage | null>(
    contentPageBySlugQuery,
    { slug }
  );
  return page ?? null;
};
